import { useEffect, useState } from "react";
import { api } from "../utils/api";
import PasswordInput from "./PasswordInput";

export default function UserManager({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [showNew, setShowNew] = useState(false);

  const [newUser, setNewUser] = useState({ username: "", password: "", email: "", role: "editor" });
  const [pwChangeFor, setPwChangeFor] = useState(null); // user object oder null
  const [newPassword, setNewPassword] = useState("");

  const reload = async () => {
    setLoading(true);
    const { data } = await api.listUsers();
    setLoading(false);
    if (data && data.ok) setUsers(data.items || []);
    else setError("Konnte Nutzer nicht laden.");
  };
  useEffect(() => { reload(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setError(""); setMessage("");
    const { data, status } = await api.createUser(newUser);
    if (data && data.ok) {
      setMessage(`Nutzer „${data.item.username}" angelegt.`);
      setShowNew(false);
      setNewUser({ username: "", password: "", email: "", role: "editor" });
      await reload();
    } else if (status === 409) setError("Benutzername bereits vergeben.");
    else setError(data?.message || "Anlegen fehlgeschlagen.");
  };

  const changeRole = async (u, role) => {
    setError(""); setMessage("");
    const { data } = await api.updateUser(u.id, { role });
    if (data && data.ok) { setMessage(`Rolle von ${u.username} geändert.`); await reload(); }
    else setError(data?.message || "Rolle konnte nicht geändert werden.");
  };

  const setPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { setError("Passwort mind. 8 Zeichen."); return; }
    const { data } = await api.updateUser(pwChangeFor.id, { password: newPassword });
    if (data && data.ok) { setMessage(`Passwort von ${pwChangeFor.username} geändert.`); setPwChangeFor(null); setNewPassword(""); }
    else setError(data?.message || "Fehler beim Setzen des Passworts.");
  };

  const remove = async (u) => {
    if (!window.confirm(`Nutzer „${u.username}" wirklich löschen?`)) return;
    const { data } = await api.deleteUser(u.id);
    if (data && data.ok) { setMessage("Nutzer gelöscht."); await reload(); }
    else setError(data?.message || "Löschen nicht möglich.");
  };

  return (
    <div data-testid="user-manager">
      <div className="admin-list-header">
        <h2 className="admin-list-title">{users.length} Benutzer</h2>
        <button type="button" onClick={() => setShowNew(!showNew)} className="admin-btn admin-btn-primary" data-testid="user-new-btn">
          {showNew ? "Abbrechen" : "+ Neuer Benutzer"}
        </button>
      </div>

      {message && <p className="admin-success">{message}</p>}
      {error && <p className="admin-error">{error}</p>}

      {showNew && (
        <form onSubmit={create} className="admin-form" data-testid="user-new-form" style={{ background: "#FBF8F3", padding: "1rem", borderRadius: 10, marginBottom: "1rem" }}>
          <div className="admin-two-col">
            <label className="admin-label">
              Benutzername
              <input type="text" value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} required data-testid="user-username-input"/>
            </label>
            <label className="admin-label">
              E-Mail
              <input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="user-email-input"/>
            </label>
          </div>
          <div className="admin-two-col">
            <label className="admin-label">
              Passwort (mind. 8 Zeichen)
              <PasswordInput value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} minLength={8} autoComplete="new-password" required data-testid="user-password-input"/>
            </label>
            <label className="admin-label">
              Rolle
              <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} data-testid="user-role-input">
                <option value="editor">Mitarbeiterin (nur News + Sprechzeiten)</option>
                <option value="admin">Admin (alles)</option>
              </select>
            </label>
          </div>
          <button type="submit" className="admin-btn admin-btn-primary" data-testid="user-create-submit">Anlegen</button>
        </form>
      )}

      {loading ? <p>Lade…</p> : users.length === 0 ? (
        <p className="admin-empty">Keine Benutzer.</p>
      ) : (
        <ul className="admin-news-list" data-testid="user-list">
          {users.map((u) => (
            <li key={u.id} className="admin-news-item" data-testid={`user-item-${u.id}`}>
              <div className="admin-news-item-main">
                <div className="admin-news-item-header">
                  <span className={`admin-status-badge ${u.role === "admin" ? "status-active" : "status-scheduled"}`}>
                    {u.role === "admin" ? "Admin" : "Mitarbeiterin"}
                  </span>
                  <strong className="admin-news-item-subject">{u.username}</strong>
                  {u.id === currentUser?.id && <span className="admin-align-hint">(du)</span>}
                </div>
                <div className="admin-news-item-period">
                  <span>E-Mail: {u.email || <em>—</em>}</span>
                </div>
              </div>
              <div className="admin-news-item-actions">
                {u.id !== currentUser?.id && (
                  <button type="button" onClick={() => changeRole(u, u.role === "admin" ? "editor" : "admin")} className="admin-btn admin-btn-ghost" data-testid={`user-role-toggle-${u.id}`}>
                    {u.role === "admin" ? "→ Mitarbeiterin" : "→ Admin"}
                  </button>
                )}
                <button type="button" onClick={() => setPwChangeFor(u)} className="admin-btn admin-btn-ghost" data-testid={`user-pw-${u.id}`}>Passwort ändern</button>
                {u.id !== currentUser?.id && (
                  <button type="button" onClick={() => remove(u)} className="admin-btn admin-btn-danger" data-testid={`user-delete-${u.id}`}>Löschen</button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {pwChangeFor && (
        <div className="cropper-overlay" data-testid="pw-modal">
          <div className="cropper-modal" style={{ padding: "1.5rem", maxWidth: 480 }}>
            <h2 className="admin-list-title">Passwort ändern für „{pwChangeFor.username}&#8220;</h2>
            <form onSubmit={setPassword} className="admin-form" style={{ marginTop: "1rem" }}>
              <label className="admin-label">
                Neues Passwort (mind. 8 Zeichen)
                <PasswordInput value={newPassword} onChange={(e) => setNewPassword(e.target.value)} minLength={8} required autoFocus autoComplete="new-password" data-testid="pw-new-input"/>
              </label>
              <div className="admin-actions-row">
                <button type="submit" className="admin-btn admin-btn-primary" data-testid="pw-save">Speichern</button>
                <button type="button" onClick={() => { setPwChangeFor(null); setNewPassword(""); }} className="admin-btn admin-btn-ghost" data-testid="pw-cancel">Abbrechen</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
